// Undo / redo for the form builder page.
//
// The builder's savable page state (questions, title, theme; the same state
// "Unsaved changes" and the local backup use) is recorded after every change.
// Undo puts the previous state back; redo the next one. History belongs to one
// page: it starts empty when another page (or version) is loaded, and is not
// kept after a reload.
//
// Typing is one step: changes to the same text box less than a second apart
// are merged, and so are changes less than 300 ms apart (a color being
// dragged, or one click that updates the page twice).

import { useCallback, useEffect, useReducer, useRef } from "react";

export const MAX_UNDO_STEPS = 50;
const TYPING_PAUSE_MS = 1000;
const SAME_ACTION_MS = 300;

// The text box being typed in, if any.
function editableTarget() {
    const el = typeof document !== "undefined" ? document.activeElement : null;
    if (!el) return null;
    if (el.isContentEditable || el.tagName === "TEXTAREA") return el;
    if (el.tagName === "INPUT" && !["checkbox", "radio", "button", "submit", "file", "range", "color"].includes(el.type)) return el;
    return null;
}

const labelOf = (field) => {
    const text = String(field?.label || field?.type || "question").replace(/\s+/g, " ").trim();
    return text.length > 40 ? `${text.slice(0, 40)}...` : text;
};
const uidOf = (field) => field?.field_uid || field?.id;

// Short description of the change from `before` to `after`, e.g.
// `adding "Email"`, `deleting "Phone"`, `the theme`.
export function describeChange(before, after) {
    if (!before || !after) return "";
    const a = before.fields || [];
    const b = after.fields || [];
    if (b.length > a.length) {
        const added = b.find(f => !a.some(x => uidOf(x) === uidOf(f)));
        return b.length - a.length === 1 && added ? `adding "${labelOf(added)}"` : "adding questions";
    }
    if (b.length < a.length) {
        const removed = a.find(f => !b.some(x => uidOf(x) === uidOf(f)));
        return a.length - b.length === 1 && removed ? `deleting "${labelOf(removed)}"` : "deleting questions";
    }
    const sameSet = a.every(f => b.some(x => uidOf(x) === uidOf(f)));
    if (sameSet && a.some((f, i) => uidOf(f) !== uidOf(b[i]))) return "moving a question";
    if ((before.formTitle ?? "") !== (after.formTitle ?? "")) return "the title change";
    if (JSON.stringify(before.theme || {}) !== JSON.stringify(after.theme || {})) return "the theme change";
    const changed = b.find(f => {
        const old = a.find(x => uidOf(x) === uidOf(f));
        return old && old !== f;
    });
    return changed ? `changes to "${labelOf(changed)}"` : "the last change";
}

// snapshot:    the current page state ({ fields, formTitle, theme })
// stateString: the same state serialized (a change = a different string)
// pageKey:     which page/version the state belongs to
// recording:   false while a page is loading (loads are not undo steps)
// apply:       puts a recorded snapshot back on screen
export function useUndoHistory({ snapshot, stateString, pageKey, recording, apply }) {
    const ref = useRef({
        key: null, past: [], future: [],
        last: null, lastString: null, lastTime: 0, lastTarget: null, applied: false, skipUntil: 0,
    });
    const [, rerender] = useReducer(n => n + 1, 0);

    // Runs after every render; cheap unless the state string changed.
    useEffect(() => {
        const h = ref.current;
        if (h.key !== pageKey) {
            Object.assign(h, { key: pageKey, past: [], future: [], last: snapshot, lastString: stateString, lastTime: 0, lastTarget: null, applied: false, skipUntil: 0 });
            rerender();
            return;
        }
        // Just put back by undo/redo: that is the current state now.
        if (h.applied) {
            h.applied = false;
            h.last = snapshot;
            h.lastString = stateString;
            return;
        }
        if (stateString === h.lastString) {
            h.last = snapshot; // same content, newest objects
            return;
        }
        if (!recording || Date.now() < h.skipUntil) {
            h.skipUntil = 0;
            h.last = snapshot;
            h.lastString = stateString;
            return;
        }
        const now = Date.now();
        const target = editableTarget();
        const merge = h.past.length > 0 && (
            (target && target === h.lastTarget && now - h.lastTime < TYPING_PAUSE_MS)
            || now - h.lastTime < SAME_ACTION_MS
        );
        if (!merge) {
            h.past.push({ state: h.last, string: h.lastString });
            if (h.past.length > MAX_UNDO_STEPS) h.past.shift();
        }
        h.future = [];
        h.last = snapshot;
        h.lastString = stateString;
        h.lastTime = now;
        h.lastTarget = target;
        rerender();
    });

    const step = useCallback((from, to) => {
        const h = ref.current;
        if (!h[from].length) return false;
        const entry = h[from].pop();
        h[to].push({ state: h.last, string: h.lastString });
        // What is put back is not a new change.
        h.last = entry.state;
        h.lastString = entry.string;
        h.applied = true;
        h.lastTime = 0;
        h.lastTarget = null;
        apply(entry.state);
        rerender();
        return true;
    }, [apply]);

    const undo = useCallback(() => step("past", "future"), [step]);
    const redo = useCallback(() => step("future", "past"), [step]);

    // A change already saved on the server (not an edit): apply it to every
    // recorded state too, and do not record it as a step.
    const applyToHistory = useCallback((transform) => {
        const h = ref.current;
        const map = (entry) => ({ ...entry, state: { ...entry.state, fields: transform(entry.state.fields) } });
        h.past = h.past.map(map);
        h.future = h.future.map(map);
        h.skipUntil = Date.now() + 1000;
    }, []);

    const h = ref.current;
    const topPast = h.past[h.past.length - 1];
    const topFuture = h.future[h.future.length - 1];
    return {
        canUndo: h.past.length > 0,
        canRedo: h.future.length > 0,
        undoLabel: topPast ? describeChange(topPast.state, h.last) : "",
        redoLabel: topFuture ? describeChange(h.last, topFuture.state) : "",
        undo,
        redo,
        applyToHistory,
    };
}
